import React, { useEffect, useState } from "react";
import { CheckCircle, WarningCircle, Info, Buildings, Gauge } from "@phosphor-icons/react";
import { api, API } from "../../lib/api";
import WhyThisMatters from "../WhyThisMatters";
import { PlatformNotice } from "../RequiredNotices";
import CourtDatesPanel from "../CourtDatesPanel";

const LEVEL_COLOR = {
  not_started: "status-not-started",
  needs_info: "status-needs-info",
  in_progress: "status-in-progress",
  organized: "status-organized",
  complete: "status-complete",
};

const LEVEL_COLOR_FALLBACK = "status-not-started";

function ScoreRing({ total, colorClass }) {
  const r = 56;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, total)) / 100;
  const style = getComputedStyle(document.documentElement);
  const readCss = (name) => style.getPropertyValue(name).trim() || "#111827";
  const strokeVar = {
    "status-not-started": readCss("--status-not-started"),
    "status-needs-info": readCss("--status-needs-info"),
    "status-in-progress": readCss("--status-in-progress"),
    "status-organized": readCss("--status-organized"),
    "status-complete": readCss("--status-complete"),
  }[colorClass];
  return (
    <svg width="140" height="140" viewBox="0 0 140 140" data-testid="score-ring">
      <circle cx="70" cy="70" r={r} fill="none" stroke="#e5e7eb" strokeWidth="10" />
      <circle
        cx="70" cy="70" r={r} fill="none" stroke={strokeVar} strokeWidth="10" strokeLinecap="round"
        strokeDasharray={`${c * pct} ${c}`}
        transform="rotate(-90 70 70)"
        style={{ transition: "stroke-dasharray 600ms ease" }}
      />
      <text x="70" y="76" textAnchor="middle" fontFamily="'Cormorant Garamond', Georgia, serif" fontSize="36" fontWeight="600" fill="#111827">{total}</text>
      <text x="70" y="98" textAnchor="middle" fontFamily="'IBM Plex Sans', sans-serif" fontSize="10" letterSpacing="2" fill="#6b7280">of 100</text>
    </svg>
  );
}

function CategoryRow({ c }) {
  const pct = c.max > 0 ? Math.round((c.earned / c.max) * 100) : 0;
  return (
    <div className="border border-slate-200 -mt-px p-4" data-testid={`score-cat-${c.name}`}>
      <div className="flex items-baseline justify-between mb-2">
        <div className="font-serif-display text-lg text-slate-900">{c.name}</div>
        <div className="tabular-nums text-sm text-slate-700">{c.earned}<span className="text-slate-400"> / {c.max}</span></div>
      </div>
      <div className="h-1.5 bg-slate-100">
        <div className="h-1.5 bg-slate-900" style={{ width: `${pct}%`, transition: "width 500ms ease" }} />
      </div>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-1 text-xs">
        {c.subitems.map((s) => {
          const done = s.earned >= s.max;
          const partial = s.earned > 0 && s.earned < s.max;
          const cls = done ? "status-complete" : partial ? "status-in-progress" : "status-needs-info";
          const Icon = done ? CheckCircle : partial ? Info : WarningCircle;
          return (
            <div key={s.key} className={`flex items-center gap-2 px-2 py-1 ${cls}`}>
              <Icon weight="duotone" className="w-3.5 h-3.5" />
              <span className="flex-1 truncate">{s.label}</span>
              <span className="tabular-nums">{s.earned}/{s.max}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ReviewTab({ caseData }) {
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/cases/${caseData.case_id}/score`);
        if (active) setScore(r.data);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [caseData.updated_at, caseData.case_id]);

  const colorClass = score ? (LEVEL_COLOR[score.level.color] || LEVEL_COLOR_FALLBACK) : LEVEL_COLOR_FALLBACK;

  return (
    <div className="space-y-8">
      <div className="flex items-baseline justify-between">
        <div>
          <h2 className="font-serif-display text-3xl text-slate-900">Claim Preparation Score</h2>
          <p className="text-slate-600 mt-1 max-w-3xl">
            A deterministic 0–100 measure of how organized and consultation-ready your claim information is.
          </p>
        </div>
        <span className="label-tiny">Step 6 of 8</span>
      </div>

      {/* Mandatory notice per spec */}
      <div className="border border-slate-200 bg-slate-50 p-4 text-xs text-slate-700" data-testid="score-notice">
        <Info weight="duotone" className="w-4 h-4 inline-block mr-2 -mt-0.5" />
        The Claim Preparation Score measures information completeness and organization only. It does not assess legal
        merit, credibility, claim value, or likelihood of success.
      </div>

      {loading || !score ? (
        <div className="border border-slate-200 p-10 text-center label-tiny">Computing score…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
            <div className="flex flex-col items-center md:items-start gap-2">
              <ScoreRing total={score.total} colorClass={colorClass} />
              <div className="mt-2" data-testid="readiness-label">
                <div className="label-tiny">Readiness</div>
                <div className={`font-serif-display text-2xl leading-tight text-slate-900`} data-testid="score-level-text">
                  {score.level.label}
                </div>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <span className={`px-2 py-0.5 text-xs font-mono-tech uppercase tracking-wider ${colorClass}`} data-testid="score-level">
                  Range {score.level.range[0]}–{score.level.range[1]}
                </span>
                <span className="label-tiny">Formula {score.formula_version}</span>
              </div>
            </div>
            <div className="md:col-span-2 grid grid-cols-1 gap-0">
              {score.categories.map((c) => <CategoryRow key={c.name} c={c} />)}
            </div>
          </div>

          {score.actions.length > 0 && (
            <div className="border border-slate-200 p-6" data-testid="score-actions">
              <div className="flex items-center gap-2 mb-3">
                <Gauge weight="duotone" className="w-5 h-5 text-slate-700" />
                <h3 className="font-serif-display text-xl text-slate-900">Raise your score</h3>
                <span className="label-tiny">{score.actions.length} suggestions</span>
              </div>
              <ol className="space-y-2 text-sm text-slate-700 list-decimal list-inside">
                {score.actions.slice(0, 8).map((a, i) => (
                  <li key={a.key || i} data-testid={`score-action-${i}`}>
                    <span className="font-medium">+{a.points} pts</span> · {a.action}
                  </li>
                ))}
              </ol>
            </div>
          )}
        </>
      )}

      <CourtDatesPanel caseId={caseData.case_id} />

      <div className="border border-slate-200 p-6" data-testid="fee-waiver-block">
        <div className="flex items-center gap-2 mb-2">
          <Buildings weight="duotone" className="w-5 h-5 text-slate-700" />
          <h3 className="font-serif-display text-xl text-slate-900">Fee Waiver Information</h3>
        </div>
        <p className="text-slate-700 text-sm leading-relaxed">
          Check with your court to see if you qualify for a fee waiver before filing. Courts may offer fee waivers based on
          income and financial circumstances.
        </p>
        <a
          href="https://www.uscourts.gov/services-forms/forms/application-to-proceed-in-district-court-without-prepaying-fees-or-costs"
          target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1.5 mt-3 text-sm font-medium text-slate-900 underline underline-offset-4"
          data-testid="fee-waiver-link"
        >
          Find your court&apos;s fee waiver information
        </a>
        <p className="text-xs text-slate-500 mt-3">
          Note: Eligibility requirements vary by court. This tool does not determine eligibility.
        </p>
      </div>

      <PlatformNotice className="border-t border-slate-200 pt-6" />
      <ClaimReadyReports caseId={caseData.case_id} />
      <WhyThisMatters step="review" />
    </div>
  );
}

function ClaimReadyReports({ caseId }) {
  const [reports, setReports] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/cases/${caseId}/claimready-reports`); setReports(r.data); }
    finally { setLoading(false); }
  }, [caseId]);

  React.useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      await api.post(`/cases/${caseId}/claimready-reports`);
      await load();
    } catch (e) {
      // no-op; UI shows list unchanged
    } finally { setBusy(false); }
  };

  const confirm = async (report_id) => {
    setBusy(true);
    try {
      await api.post(`/cases/${caseId}/claimready-reports/${report_id}/confirm`);
      await load();
    } finally { setBusy(false); }
  };

  const downloadPdf = async (report_id, version) => {
    const r = await fetch(`${API}/cases/${caseId}/claimready-reports/${report_id}/pdf`, { credentials: "include" });
    if (!r.ok) return;
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `claimready_v${version}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <section className="border border-slate-200 p-6" data-testid="claimready-reports">
      <div className="flex items-center justify-between gap-4 mb-3 flex-wrap">
        <div>
          <h3 className="font-serif-display text-2xl text-slate-900">ClaimReady Report</h3>
          <p className="text-xs text-slate-500">Immutable, versioned consultation-prep snapshot. Not a pleading. Not legal advice.</p>
        </div>
        <button onClick={generate} disabled={busy}
          className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm disabled:opacity-60"
          data-testid="generate-report-btn">
          {busy ? "Generating…" : "Generate new version"}
        </button>
      </div>
      {loading ? (
        <div className="label-tiny">Loading reports…</div>
      ) : reports.length === 0 ? (
        <div className="text-sm text-slate-500">No reports yet.</div>
      ) : (
        <div className="divide-y divide-slate-200">
          {reports.map((r) => (
            <div key={r.report_id} className="py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`report-${r.report_id}`}>
              <div>
                <div className="font-medium">Version {r.version} <span className="label-tiny ml-2">{r.review_status}</span></div>
                <div className="text-xs text-slate-500 tabular-nums">Generated {new Date(r.generated_at).toLocaleString()} · {r.model} · template {r.template_version}</div>
              </div>
              <div className="flex gap-2">
                {r.review_status !== "confirmed" && (
                  <button onClick={() => confirm(r.report_id)} disabled={busy}
                    className="px-3 py-1.5 text-xs border border-slate-300 hover:bg-slate-50"
                    data-testid={`confirm-report-${r.report_id}`}>
                    Confirm
                  </button>
                )}
                <button onClick={() => downloadPdf(r.report_id, r.version)}
                  className="px-3 py-1.5 text-xs bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid={`download-report-${r.report_id}`}>
                  Download PDF
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
